import { IPromise, IScope, ITimeoutService } from "angular";
import { HotkeysProvider } from "angular-hotkeys";
import { StateService } from "@uirouter/angularjs";
import { IStateInit } from "@gtmhub/core/routing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management/models";
import { ITag, ITagsQueryParams } from "@gtmhub/tags/models";
import { TagService } from "@gtmhub/tags/tag.service";
import { IPlanningSessionStats, Session } from "@webapp/sessions/models/sessions.model";
import { PlanningSessionsActions } from "../redux/session-actions";
import { ISessionsStoreState } from "../redux/session-reducer";
import { PlanningSessionService } from "../services/planning-session.service";

interface IPlanningSessionOverviewRootScopeService extends IGtmhubRootScopeService {
  cast: boolean;
}

export interface IPlanningSessionOverviewStateParamsService {
  planningSessionId: string;
  tags?: string;
}
export interface IPlanningSessionOverviewScope extends IScope, IOverviewReduxScope, IExtendedRootScopeService {
  stats: IPlanningSessionStats;
  indicators: {
    stats?: IIndicator;
    searching?: IIndicator;
  };
  tags: ITag[];
  session: Session;
  loadTags(tagTitle: string): void;
  filterByTag(): void;
}

interface IOverviewReduxScope {
  sessionIdMap: Record<string, Session>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: ISessionsStoreState): IOverviewReduxScope => {
  return {
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
  };
};

export class PlanningSessionOverviewCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "PlanningSessionService",
    "TagService",
    "$timeout",
    "UIErrorHandlingService",
    "hotkeys",
    "$ngRedux",
    "PlanningSessionsActions",
    "ApmSpanService",
  ];

  constructor(
    private $rootScope: IPlanningSessionOverviewRootScopeService,
    private $scope: IPlanningSessionOverviewScope,
    private $stateService: StateService,
    private $stateParams: IPlanningSessionOverviewStateParamsService,
    private planningSessionService: PlanningSessionService,
    private tagService: TagService,
    private $timeout: ITimeoutService,
    private uiErrorHandlingService: UIErrorHandlingService,
    public hotkeys: HotkeysProvider,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private apmSpanService: ApmSpanService
  ) {
    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));

    this.$scope.indicators = {};
    this.initTags();
    this.initCastCapabilities();

    this.$scope.loadTags = this.loadTags.bind(this);
    this.$scope.filterByTag = this.filterByTag.bind(this);
  }

  public stateInit(): IPromise<unknown> {
    if (this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM) {
      this.navigateToSessionGridView();
      return;
    }

    return this.init(this.$stateParams.tags);
  }

  private init(tags: string): IPromise<unknown> {
    return this.apmSpanService.dataLoadSpan("session-statistics-init", () => {
      return this.loadResuxSessions().then(
        () => {
          return this.loadSessionStats(tags).then(() => {
            this.$scope.$on("goalsChanged", this.onGoalsChanged.bind(this));
          });
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  }

  private initTags(): void {
    this.$scope.tags = [];
    if (this.$stateParams.tags) {
      this.setParams(this.$stateParams);
    }
  }

  private initCastCapabilities(): void {
    // keyboard stroke for enabling cast capabilities
    this.$rootScope.cast = false;
    this.hotkeys.add({
      combo: "shift+c",
      callback: () => {
        this.$rootScope.cast = !this.$rootScope.cast;
      },
    });
  }

  private navigateToSessionGridView(): void {
    this.$stateService.go("^.all");
  }

  private setParams(params: IPlanningSessionOverviewStateParamsService): void {
    const tags = params.tags.split(",");

    for (let i = 0; i < tags.length; i++) {
      this.$scope.tags.push({
        name: tags[i],
        title: tags[i],
      });
    }
  }

  private filterByTag(): void {
    const params = [];
    this.$timeout(() => {
      for (let i = 0; i < this.$scope.tags.length; i++) {
        params.push(this.$scope.tags[i].title);
      }

      this.$stateService.go("gtmhub.goals.overview", { tags: params.toString() }, { reload: false });
    }, 10);
  }

  private loadTags(tagTitle: string): IPromise<ITag[]> {
    this.$scope.indicators.searching = { progress: true };
    const params: ITagsQueryParams = {
      title: tagTitle,
    };
    return this.tagService.getTags(params).then(
      (collection) => {
        delete this.$scope.indicators.searching;
        return collection.tags;
      },
      () => {
        return [];
      }
    );
  }

  private loadResuxSessions(): IPromise<void> {
    return this.apmSpanService.dataLoadSpan("load-redux-sessions", () => {
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

      return this.$scope.watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      );
    });
  }

  private loadSessionStats(tags: string): IPromise<void> {
    this.$scope.indicators.stats = { progress: true };

    return this.apmSpanService
      .dataLoadSpan("load-session-stats", () => this.planningSessionService.stats(this.$stateParams.planningSessionId, { tags }))
      .then((stats: IPlanningSessionStats) => {
        this.$scope.session = this.$scope.sessionIdMap[this.$stateParams.planningSessionId];
        this.$scope.stats = stats;
        this.$scope.stats.progress = stats.progress * 100;

        delete this.$scope.indicators.stats;
      });
  }

  private onGoalsChanged(): void {
    this.loadSessionStats(null);
  }
}
